import { Controller } from "@hotwired/stimulus"
import { createAlert } from "../scripts/createAlert"

// Multi-record Manual Entry: same dropdown add / disable-already-added pattern as
// record-book-buckets (PDF import), but each add reveals Top N entry fields.
export default class extends Controller {
  static targets = [
    "recordSelect",
    "newBucketName",
    "recordScope",
    "recordsList",
    "recordTemplate",
    "emptyState",
    "freeTextAdd"
  ]

  static values = {
    topN: Number,
    hasMasterList: Boolean
  }

  connect() {
    this.nextIndex = 0
    this.syncRecordSelectOptions()
    this.toggleEmptyState()
  }

  addRecord() {
    if (this.hasMasterListValue) {
      this.addFromMasterSelect()
    } else {
      this.addFromFreeText()
    }
  }

  addFromMasterSelect() {
    if (!this.hasRecordSelectTarget) return

    const select = this.recordSelectTarget
    const value = select.value.trim()
    if (!value) return

    if (this.addedRecordValues().includes(value)) {
      createAlert("danger", "That record is already in the list.", true)
      select.value = ""
      return
    }

    this.appendRecordCard({
      recordList: value,
      heading: value
    })
    select.value = ""
    this.syncRecordSelectOptions()
    this.toggleEmptyState()
  }

  addFromFreeText() {
    const scope = this.hasRecordScopeTarget ? this.recordScopeTarget.value.trim() : ""
    const title = this.hasNewBucketNameTarget ? this.newBucketNameTarget.value.trim() : ""
    if (!scope || !title) {
      createAlert("danger", "Choose a scope and enter a title first.", true)
      return
    }

    const key = `${scope}::${title}`
    if (this.addedFreeTextKeys().includes(key)) {
      createAlert("danger", "That record is already in the list.", true)
      return
    }

    const scopeLabel = this.hasRecordScopeTarget
      ? (this.recordScopeTarget.selectedOptions[0]?.text || scope)
      : scope

    this.appendRecordCard({
      recordScope: scope,
      title: title,
      heading: `${scopeLabel} — ${title}`,
      freeTextKey: key
    })

    if (this.hasNewBucketNameTarget) this.newBucketNameTarget.value = ""
    this.toggleEmptyState()
  }

  removeRecord(event) {
    const card = event.currentTarget.closest("[data-manual-record-card]")
    if (card) card.remove()
    this.syncRecordSelectOptions()
    this.toggleEmptyState()
  }

  // Same idea as PDF buckets: disable dropdown options already on the page.
  syncRecordSelectOptions() {
    if (!this.hasRecordSelectTarget) return

    const added = new Set(this.addedRecordValues())
    Array.from(this.recordSelectTarget.options).forEach((option) => {
      if (!option.value) return
      option.disabled = added.has(option.value)
    })
  }

  addedRecordValues() {
    return Array.from(this.recordsListTarget.querySelectorAll("[data-manual-record-list]"))
      .map((input) => input.value)
  }

  addedFreeTextKeys() {
    return Array.from(this.recordsListTarget.querySelectorAll("[data-manual-free-text-key]"))
      .map((node) => node.dataset.manualFreeTextKey)
  }

  appendRecordCard({ recordList, recordScope, title, heading, freeTextKey }) {
    if (!this.hasRecordTemplateTarget) return

    const index = this.nextIndex
    this.nextIndex += 1

    const fragment = this.recordTemplateTarget.content.cloneNode(true)
    const card = fragment.querySelector("[data-manual-record-card]")
    if (!card) return

    card.querySelectorAll("[name]").forEach((input) => {
      input.name = input.name.replaceAll("__INDEX__", String(index))
    })
    card.querySelectorAll("[id]").forEach((node) => {
      node.id = node.id.replaceAll("__INDEX__", String(index))
    })
    card.querySelectorAll("[for]").forEach((node) => {
      node.htmlFor = node.htmlFor.replaceAll("__INDEX__", String(index))
    })

    const headingNode = card.querySelector("[data-manual-record-heading]")
    if (headingNode) headingNode.textContent = heading || ""

    if (recordList) {
      card.querySelectorAll("[data-manual-free-text-fields]").forEach((node) => node.remove())
      const listInput = card.querySelector("[data-manual-record-list]")
      if (listInput) listInput.value = recordList
    } else {
      card.querySelectorAll("[data-manual-master-fields]").forEach((node) => node.remove())
      const freeTextWrap = card.querySelector("[data-manual-free-text-fields]")
      if (freeTextWrap) freeTextWrap.dataset.manualFreeTextKey = freeTextKey || ""
      const scopeInput = card.querySelector("[data-manual-record-scope]")
      if (scopeInput) scopeInput.value = recordScope || ""
      const titleInput = card.querySelector("[data-manual-record-title]")
      if (titleInput) titleInput.value = title || ""
    }

    this.recordsListTarget.prepend(card)
  }

  toggleEmptyState() {
    if (!this.hasEmptyStateTarget) return
    const hasCards = this.recordsListTarget.querySelector("[data-manual-record-card]")
    this.emptyStateTarget.classList.toggle("d-none", !!hasCards)
  }
}
